import { buildSectionSelector, normalizeSectionId, sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, onDocumentReady } from '@theme/utilities';

/**
 * Hydrates a section using the Section Rendering API preserving states.
 * Only updates elements with data-hydration-key attribute. This ensures that non-targeted nodes do not have their markup/state updated unnecessarily.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
async function hydrateSection(sectionId, url) {
  const normalizedId = normalizeSectionId(sectionId);
  const section = document.getElementById(buildSectionSelector(normalizedId));

  if (!section || section.dataset.hydrated === 'true') {
    return;
  }

  section.dataset.hydrationState = 'loading';
  delete section.dataset.hydrationError;
  await sectionRenderer.renderSection(normalizedId, { cache: false, url, mode: 'hydration' });

  section.dataset.hydrated = 'true';
  section.dataset.hydrationState = 'ready';
}

/**
 * Hydrates a section using the Section Rendering API preserving states, when
 * the DOM is ready.
 *
 * @param {string} sectionId - The section ID to hydrate
 * @param {URL} [url] - The URL to render the section from
 */
export async function hydrate(sectionId, url) {
  return new Promise((resolve) => {
    onDocumentReady(() => {
      requestIdleCallback(async () => {
        try {
          await hydrateSection(sectionId, url);
        } catch (error) {
          const section = document.getElementById(buildSectionSelector(normalizeSectionId(sectionId)));
          if (section) {
            const reason = error?.name === 'AbortError' ? 'aborted' : error?.name === 'TypeError' &&
              ['Failed to fetch', 'Load failed', 'NetworkError when attempting to fetch resource.'].includes(error.message)
              ? 'network' : 'render';
            section.dataset.hydrationState = 'error';
            section.dataset.hydrationError = reason;
            section.dispatchEvent(new CustomEvent('section:hydration:error', { bubbles: true, detail: { reason } }));
          }
        } finally {
          resolve();
        }
      });
    });
  });
}
